// src/pages/ConsultantReport.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SummaryApi from '../common/index';
import { Search, Eye, EyeOff, Edit, Download } from 'lucide-react';
import * as XLSX from 'xlsx'; // ← Add this import (install: npm install xlsx)

const ConsultantReport = () => {
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [visiblePasswords, setVisiblePasswords] = useState({});

  const itemsPerPage = 25;
  const navigate = useNavigate();

  const displayValue = (value) => {
    if (value === null || value === undefined || value === '' || value === '-') {
      return 'N/A';
    }
    return value;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(SummaryApi.Getallconsultants.url, {
          method: SummaryApi.Getallconsultants.method || 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.status === true) {
          if (Array.isArray(result.data)) {
            const formattedData = result.data
              // Only keep Consultancy members (case-insensitive check)
              .filter(item => 
                item.member_type && 
                item.member_type.toLowerCase() === 'consultancy'
              )
              .map((item) => ({
                ...item,
                registered_on:
                  item.created_date && item.created_date !== '0000-00-00 00:00:00.000000'
                    ? new Date(item.created_date).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : 'N/A',
                registered_date:
                  item.created_date && item.created_date !== '0000-00-00 00:00:00.000000'
                    ? new Date(item.created_date).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })
                    : 'N/A',
                full_name: item.full_name || 'N/A',
                consultancy_code: item.consultancy_code || 'N/A',
                mobile: item.mobile_number1 || item.mobile_number2 || 'N/A',
                consultancy_name: item.consultancy_name || 'N/A',
                city: item.city || 'N/A',
                member_type: item.member_type || 'N/A',
                is_active: item.is_active === '1' ? 'Active' : 'Inactive',
                user_name: item.user_name || 'N/A',
                password: item.password || 'N/A',
              }));

            setConsultants(formattedData);
          } else {
            setConsultants([]);
          }
        } else {
          setError(result.message || 'Failed to load consultant records');
          setConsultants([]);
        }
      } catch (err) {
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const parseDate = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return null;
    const parts = dateStr.split(/[-/]/);
    if (parts.length !== 3) return null;
    const [dd, mm, yyyy] = parts;
    return new Date(`${yyyy}-${mm}-${dd}`);
  };

  const filteredConsultants = consultants.filter((consultant) => {
    const searchLower = searchTerm.toLowerCase().trim();

    const matchesSearch =
      (consultant.full_name || '').toLowerCase().includes(searchLower) ||
      (consultant.consultancy_code || '').toLowerCase().includes(searchLower) ||
      (consultant.mobile || '').toLowerCase().includes(searchLower) ||
      (consultant.consultancy_name || '').toLowerCase().includes(searchLower) ||
      (consultant.city || '').toLowerCase().includes(searchLower) ||
      (consultant.member_type || '').toLowerCase().includes(searchLower) ||
      (consultant.user_name || '').toLowerCase().includes(searchLower) ||
      (consultant.password || '').toLowerCase().includes(searchLower);

    let matchesDate = true;
    if (fromDate) {
      const from = new Date(fromDate);
      matchesDate = matchesDate && parseDate(consultant.registered_date) >= from;
    }
    if (toDate) {
      const to = new Date(toDate);
      matchesDate = matchesDate && parseDate(consultant.registered_date) <= to;
    }

    return matchesSearch && matchesDate;
  });

  const totalPages = Math.ceil(filteredConsultants.length / itemsPerPage);
  const currentConsultants = filteredConsultants.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const togglePasswordVisibility = (adminId) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [adminId]: !prev[adminId],
    }));
  };

  const handleEdit = (consultant) => {
    const idToEncode = consultant.admin_id;
    if (!idToEncode || idToEncode === 'N/A') {
      alert('Cannot edit: Admin ID not available');
      return;
    }
    const encoded = btoa(String(idToEncode));
    navigate(`/create-consultant/${encoded}`);
  };

  const handleExportToExcel = () => {
    if (filteredConsultants.length === 0) {
      alert('No data to export');
      return;
    }

    const exportData = filteredConsultants.map((item, index) => ({
      'S.No': index + 1,
      'Full Name': displayValue(item.full_name),
      'Mobile': displayValue(item.mobile),
      'Consultancy Name': displayValue(item.consultancy_name),
      'City': displayValue(item.city),
      'Member Type': displayValue(item.member_type),
      'Username': displayValue(item.user_name),
      'Password': displayValue(item.password),
      'Status': item.is_active,
      'Registered On': displayValue(item.registered_on),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    const colWidths = [];
    Object.keys(exportData[0] || {}).forEach((key, i) => {
      let maxWidth = key.length;
      exportData.forEach(row => {
        const val = row[key] ? String(row[key]) : '';
        if (val.length > maxWidth) maxWidth = val.length;
      });
      colWidths[i] = { wch: Math.min(maxWidth + 2, 40) };
    });
    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Consultants');

    XLSX.writeFile(workbook, `consultant-report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
  };

  return (
    <div className="max-w-[98%] mx-auto px-4 py-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-2xl font-bold text-gray-800">Consultancy Report</h2>
      </div>

      {/* Filters + Export Button */}
      <div className="mb-6 flex flex-col sm:flex-row flex-wrap justify-between items-end gap-4">
        <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[280px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search size={18} className="text-gray-400" strokeWidth={2} />
              </div>
              <input
                type="text"
                placeholder="Consultancy Name, Mobile Number, City..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1"> </label>
              <button
                onClick={clearAllFilters}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md font-medium transition"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={handleExportToExcel}
          disabled={loading || filteredConsultants.length === 0}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-medium text-white transition ${
            loading || filteredConsultants.length === 0
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          <Download size={18} />
          Export to Excel
        </button>
      </div>

      {loading && (
        <div className="text-center py-12 text-lg font-medium text-gray-700">
          Loading consultant records...
        </div>
      )}


      {/* Table */}
      <div className="overflow-x-auto bg-white shadow-lg rounded-xl border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              <th className="px-5 py-4 text-left font-semibold text-gray-800 w-16">S.No</th>
              <th className="px-5 py-4 text-left font-semibold text-gray-800">Full Name</th>
              <th className="px-5 py-4 text-left font-semibold text-gray-800">Mobile</th>
              <th className="px-5 py-4 text-left font-semibold text-gray-800">Consultancy Name</th>
              <th className="px-5 py-4 text-left font-semibold text-gray-800">City</th>
              <th className="px-5 py-4 text-left font-semibold text-gray-800">Username</th>
              <th className="px-5 py-4 text-left font-semibold text-gray-800">Password</th>
              <th className="px-5 py-4 text-left font-semibold text-gray-800">Status</th>
              <th className="px-5 py-4 text-left font-semibold text-gray-800">Registered On</th>
              <th className="px-5 py-4 text-center font-semibold text-gray-800">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100 bg-white">
            {currentConsultants.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-6 py-16 text-center text-gray-600 font-medium">
                  {consultants.length === 0
                    ? 'No consultancy records found in the system.'
                    : 'No matching consultancy records found for the current filters.\nTry adjusting the search term or date range.'}
                </td>
              </tr>
            ) : (
              currentConsultants.map((consultant, index) => (
                <tr key={consultant.admin_id || index} className="hover:bg-blue-50 transition-colors">
                  <td className="px-5 py-4 text-center font-medium text-gray-900">
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </td>
                  <td className="px-5 py-4 font-medium text-gray-900">
                    {displayValue(consultant.full_name)}
                  </td>
                  <td className="px-5 py-4">{displayValue(consultant.mobile)}</td>
                  <td className="px-5 py-4">{displayValue(consultant.consultancy_name)}</td>
                  <td className="px-5 py-4">{displayValue(consultant.city)}</td>
                  <td className="px-5 py-4 font-mono">{displayValue(consultant.user_name)}</td>

                  <td className="px-5 py-4 font-mono flex items-center gap-2">
                    {visiblePasswords[consultant.admin_id]
                      ? displayValue(consultant.password)
                      : '••••••••••'}
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility(consultant.admin_id)}
                      className="text-gray-500 hover:text-gray-700 focus:outline-none"
                      title={visiblePasswords[consultant.admin_id] ? 'Hide' : 'Show'}
                    >
                      {visiblePasswords[consultant.admin_id] ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </td>

                  <td className="px-5 py-4">
                    <span
                      className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                        consultant.is_active === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {consultant.is_active}
                    </span>
                  </td>
                  <td className="px-5 py-4">{displayValue(consultant.registered_on)}</td>

                  <td className="px-5 py-4 text-center">
                    <button
                      onClick={() => handleEdit(consultant)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-md transition shadow-sm"
                      title="Edit Consultant"
                    >
                      <Edit size={16} />
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1 || currentConsultants.length === 0}
          className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-5 py-2.5 rounded-md font-medium disabled:opacity-50 transition"
        >
          Previous
        </button>

        <span className="text-gray-700 font-medium">
          Page {currentPage} of {totalPages || 1} ({filteredConsultants.length} records)
        </span>

        <button
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages || currentConsultants.length === 0}
          className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-5 py-2.5 rounded-md font-medium disabled:opacity-50 transition"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ConsultantReport;